import { useRouter } from 'next/router';
import { useCallback, useMemo } from 'react';
import useTranslation from 'next-translate/useTranslation';
import { useQuery } from '@tanstack/react-query';

import usePlayerDetail from '~/domains/games/hooks/usePlayerDetail';
import { getSurvivorPropById } from '~/domains/contest/domains/services/props.service.api';
import { parseChartData } from '~/domains/contest/domains/quickPick/utils/parsePropToComponentProp';
import { getTeamOrPlayerImageUrl } from '~/domains/common/utils/getTeamOrPlayerImageUrl';

import parsePlayerInfo from '../../parsers/parsePlayerInfo/parsePlayerInfo';
import { useContestContext } from '../../../common/context/ContestContext/ContestContext';
import parsePlayerGroupedStats from '../../parsers/parsePlayerGroupedStats/parsePlayerGroupedStats';
import type { PlayerPick } from '../../types/playerPick';
import type { TierObject } from '../../types/tier';
import usePlayerGamesLog from '../../hooks/usePlayerGamesLog';
import type { PlayerInfo } from '../../types/player';

import GamecenterPlayerDetail from './GamecenterPlayerDetail';
import type { GamecenterPlayerDetailActionProps } from './GamecenterPlayerDetailActions/GamecenterPlayerDetailAction';

function ConnectedGamecenterPlayerDetail({
  screen,
  players,
  picks,
  onPlayerPick,
  onPickRemove,
  type,
}: {
  screen: 'playerPool' | 'picksEntryDetail' | 'entryDetail' | 'players' | 'survivor-playerPool';
  players?: PlayerPick[];
  picks?: PlayerPick[];
  onPlayerPick?: (tier: TierObject, pickId: string) => void;
  onPickRemove?: (tier: TierObject | { id: string }) => void;
  type?: 'survivor-prop' | 'others';
}) {
  const router = useRouter();
  const { t } = useTranslation('contest');
  const { playerId, propId } = router.query;
  const { contest } = useContestContext();

  const playerDetailQuery = usePlayerDetail({
    playerId: playerId as string,
  });

  const isPropEnabled = !!propId && type === 'survivor-prop';
  // eslint-disable-next-line local-rules/require-query-stale-time
  const { data: prop, isLoading: isPropLoading } = useQuery({
    queryKey: ['survivor-prop', propId],
    queryFn: () => getSurvivorPropById(propId as string),
    enabled: isPropEnabled,
  });

  const playerGamesLogQuery = usePlayerGamesLog({
    playerId: playerId as string,
    sport: contest.league.sport,
    playerPosition:
      prop?.prop.entity.player_details.position ?? playerDetailQuery.data?.attributes.position,
  });

  const parsePropDetailToPlayerInfo = useCallback(
    (propDetail) => ({
      playerId: propDetail.prop.entity_id,
      playerName: propDetail.prop.entity_name,
      playerImage: getTeamOrPlayerImageUrl(propDetail.prop.league, propDetail.prop.entity_id),
      tag: {
        team: propDetail.prop.team.name,
        position: playerDetailQuery?.data?.attributes?.position,
        jerseyNumber: `#${propDetail.prop.entity.player_details.jersey_number}`,
        injury: propDetail.prop.injury?.abbr_status,
      },
      chartProps: parseChartData(propDetail.prop.chart_data, propDetail.prop.line),
    }),
    [playerDetailQuery?.data?.attributes?.position]
  );

  const playerInfo = useMemo(() => {
    if (type === 'survivor-prop' && prop) {
      return parsePropDetailToPlayerInfo(prop);
    }

    if (playerDetailQuery.data) {
      return parsePlayerInfo(
        playerDetailQuery.data,
        contest.league.sport,
        contest?.settings?.scoreType
      );
    }
  }, [
    contest.league.sport,
    contest?.settings?.scoreType,
    parsePropDetailToPlayerInfo,
    playerDetailQuery.data,
    prop,
    type,
  ]);

  const groupedStats = useMemo(
    () =>
      playerDetailQuery.data
        ? parsePlayerGroupedStats(
            playerDetailQuery.data,
            contest.league.sport,
            contest?.settings?.scoreType
          )
        : null,
    [contest.league.sport, contest?.settings?.scoreType, playerDetailQuery.data]
  );

  const actions: GamecenterPlayerDetailActionProps[] = useMemo(() => {
    if (!picks) {
      return [];
    }

    const currentPick = picks.find((pick) => pick?.playerInfo?.playerId === playerId);
    if (
      currentPick &&
      (!currentPick?.playerInfo?.isUpcoming || currentPick?.playerInfo?.isLocked)
    ) {
      return [];
    }

    const player = players?.find((p) => p.playerInfo?.playerId === playerId);
    if ((player && !player.playerInfo?.isUpcoming) || player?.playerInfo?.isLocked) {
      return [];
    }

    const routerPush = (query: Record<string, string | number>) => {
      const newQuery = { ...router.query, ...query };
      delete newQuery.playerId;

      router.push({
        query: newQuery,
      });
    };

    switch (screen) {
      case 'survivor-playerPool': {
        if (currentPick) {
          return [
            {
              label: t('gamecenter.player.common.playerDetail.actions.remove'),
              onClick: () => {
                onPickRemove?.({ id: player.id });
                routerPush({
                  playerId: undefined,
                });
              },
              variant: 'secondary-stroke',
            },
          ];
        }

        if (player && onPlayerPick) {
          return [
            {
              label: player?.isAlternate
                ? t('gamecenter.player.common.playerDetail.actions.selectAlternate')
                : t('gamecenter.player.common.playerDetail.actions.select'),
              onClick: () => {
                onPlayerPick?.(player.tier, player.pickId);
                routerPush({
                  playerId: undefined,
                });
              },
              variant: 'primary-fill',
            },
          ];
        }

        return [];
      }
      case 'playerPool': {
        if (currentPick) {
          return [
            {
              label: t('gamecenter.player.common.playerDetail.actions.remove'),
              onClick: () => {
                onPickRemove?.(currentPick.tier);
                routerPush({
                  tierId: currentPick.tier.id,
                  tierPickIndex: currentPick.tier.pickIndex,
                });
              },
              variant: 'secondary-stroke',
            },
          ];
        }

        if (player && onPlayerPick) {
          return [
            {
              label: t('gamecenter.player.common.playerDetail.actions.draft'),
              onClick: () => {
                onPlayerPick?.(player.tier, player.pickId);
              },
              variant: 'primary-fill',
            },
          ];
        }

        return [];
      }
      case 'picksEntryDetail': {
        if (!currentPick) {
          return [];
        }

        return [
          {
            label: t('gamecenter.player.common.playerDetail.actions.remove'),
            onClick: () => {
              onPickRemove?.(currentPick.tier);
              routerPush({
                tierId: currentPick.tier.id,
                tierPickIndex: currentPick.tier.pickIndex,
              });
            },
            variant: 'secondary-stroke',
          },
          {
            label: t('gamecenter.player.common.playerDetail.actions.swap'),
            onClick: () => {
              routerPush({
                tierId: currentPick.tier.id,
                tierPickIndex: currentPick.tier.pickIndex,
              });
            },
            variant: 'primary-fill',
          },
        ];
      }
      case 'players':
      case 'entryDetail':
      default: {
        return [];
      }
    }
  }, [picks, screen, playerId, router, players, onPlayerPick, t, onPickRemove]);

  const handleClose = useCallback(() => {
    const newQuery = { ...router.query };
    delete newQuery.playerId;

    router.push(
      {
        query: newQuery,
      },
      undefined,
      { shallow: true }
    );
  }, [router]);

  return (
    <GamecenterPlayerDetail
      isOpen={!!playerId}
      onClose={handleClose}
      isLoading={
        playerDetailQuery.isLoading ||
        playerGamesLogQuery.isLoading ||
        (isPropEnabled && isPropLoading)
      }
      error={playerDetailQuery.error}
      playerInfo={playerInfo as PlayerInfo}
      headerProps={{
        playerName: playerInfo?.playerName,
        tag: playerInfo?.tag,
        playerImage: playerInfo?.playerImage,
        ...(screen === 'survivor-playerPool' && {
          useTeamAlias: true,
          teamAlias: playerInfo?.tag?.team,
          teamColor: playerDetailQuery.data?.attributes?.team?.color,
          leagueImage: `/images/leagues/${contest.league.alias}.png`,
        }),
      }}
      groupedStats={groupedStats}
      gameLogProps={{
        tableData: playerGamesLogQuery.items,
        tableFetchNextPage: playerGamesLogQuery.fetchNextPage,
        tableHasNextPage: playerGamesLogQuery.hasNextPage,
        isTableFetchingNextPage: playerGamesLogQuery.isFetchingNextPage,
      }}
      actions={actions}
      type={type}
      propTypeDisplay={contest?.settings?.props?.typeDisplay}
    />
  );
}

export default ConnectedGamecenterPlayerDetail;
